import { Tooltip, Button } from "@heroui/react";
import {
  BrandingColor,
  ExtraParam,
  BrandingPositionItem,
  BrandingPosition,
} from "app/api/product/product.type";
import { useState } from "react";
import { MdQuestionMark } from "react-icons/md";

const ExtraParamComponent = ({
  decorationOptions,
  currentBrandingType,
  selectedArtColor,
  currentBrandingPosition,
}: {
  decorationOptions: {
    [key: string]: BrandingColor & { type: string; friendlyName: string };
  };
  currentBrandingPosition: string;
  selectedArtColor: keyof BrandingColor;
  currentBrandingType: {
    name: string;
    friendlyName: string;
  };
}) => {
  // State to track selected extra values, organized by branding type and key
  const [selectedExtraValues, setSelectedExtraValues] = useState<{
    [brandingType: string]: { [key: string]: string };
  }>({});

  const handleSelection = (brandingType: string, key: string, val: string) => {
    setSelectedExtraValues((prevState) => ({
      ...prevState,
      [brandingType]: {
        ...prevState[brandingType],
        [key]: val,
      },
    }));
  };

  const currentOptions = (() => {
    const colorData = decorationOptions?.[currentBrandingType.name]?.[selectedArtColor];

    // Handle new structure with positions array (when selectedArtColor is 'positions')
    if (selectedArtColor === "positions" && Array.isArray(colorData)) {
      return colorData.find(
        (position: BrandingPositionItem) => position.value === currentBrandingPosition,
      );
    }

    // Handle legacy structure with singleColour/multiColour
    if (selectedArtColor === "singleColour" || selectedArtColor === "multiColour") {
      const brandingPosition = colorData as BrandingPosition;
      return brandingPosition?.positions?.find(
        (position: { name: string; extraParams: ExtraParam[] }) =>
          position.name === currentBrandingPosition,
      );
    }

    return undefined;
  })();

  return (
    <div>
      {currentOptions && (
        <>
          {currentOptions.extraParams.map((params: ExtraParam) => (
            <div className="flex flex-wrap my-4" key={params.key}>
              <div className="flex items-center space-x-2 w-1/3">
                <span className="text-sm md:text-base font-semibold">{params.key}</span>
                <Tooltip
                  showArrow
                  content={
                    <div className="max-w-60">
                      This is the number of positions you need your logo or artwork branded on the
                      products you order. The higher the number of branding positions, the higher
                      the branding price.
                    </div>
                  }
                >
                  <Button isIconOnly className="bg-transparent !p-1">
                    <MdQuestionMark size={20} className="text-green-600 text-sm" />
                  </Button>
                </Tooltip>
              </div>
              <div className="flex gap-x-2 mt-1">
                {params.values?.map((val: string) => (
                  <div
                    key={val}
                    className={`rounded-md cursor-pointer w-fit text-center px-2 sm:px-3 md:px-4 py-2 ${
                      selectedExtraValues[currentBrandingType.name]?.[params.key] === val
                        ? "bg-primary text-white"
                        : "border bg-white dark:text-black border-zinc-200"
                    }`}
                    onClick={() => handleSelection(currentBrandingType.name, params.key, val)}
                  >
                    {val}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default ExtraParamComponent;
